import React from 'react';

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'default';

export interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  className = '',
}) => {
  const variantClasses = {
    success: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    danger: 'bg-red-100 text-red-800 border-red-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200',
    default: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full border ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {children}
    </span>
  );
};

// Specialized badges for common use cases
export const SafetyRatingBadge: React.FC<{ rating?: string }> = ({ rating }) => {
  if (!rating) return <Badge variant="default">Not Rated</Badge>;

  const variants = {
    S: { variant: 'success' as const, text: 'Satisfactory' },
    C: { variant: 'warning' as const, text: 'Conditional' },
    U: { variant: 'danger' as const, text: 'Unsatisfactory' },
  };

  const config = variants[rating as keyof typeof variants] || {
    variant: 'default' as const,
    text: rating,
  };

  return <Badge variant={config.variant}>{config.text}</Badge>;
};

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const variant = status === 'A' || status === 'Active' ? 'success' : 'danger';
  const text = status === 'A' ? 'Active' : status === 'I' ? 'Inactive' : status;

  return <Badge variant={variant}>{text}</Badge>;
};

export const VerificationBadge: React.FC<{ verified: boolean }> = ({ verified }) => {
  return (
    <Badge variant={verified ? 'success' : 'warning'}>
      {verified ? 'Verified' : 'Unverified'}
    </Badge>
  );
};

export const SeverityBadge: React.FC<{ severity: 'critical' | 'high' | 'medium' | 'low' }> = ({
  severity,
}) => {
  const variants = {
    critical: 'danger' as const,
    high: 'warning' as const,
    medium: 'info' as const,
    low: 'default' as const,
  };

  return (
    <Badge variant={variants[severity]}>
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </Badge>
  );
};
